import React from 'react'
import {
  ChartComponent as SyncfusionChart,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  LineSeries,
  Category,
  Legend,
  Tooltip,
  ColumnSeries,
  DataLabel,
  ChartSeriesType,
  StackingColumnSeries,
  CornerRadiusModel,
} from '@syncfusion/ej2-react-charts'
import './ChartStyle.css'

interface ChartComponentProps {
  className?: string
  columnSpacing?: number
  columnWidth?: number
  cornerRadius?: CornerRadiusModel
  data: object[]
  enableTooltip?: boolean
  height?: string
  id: string
  maxXAxis?: number
  maxYAxis?: number
  name?: string
  seriesWidth?: number
  title?: string
  tooltipFormat?: string
  tooltipTemplate?: string | ((args: any) => JSX.Element)
  type?: ChartSeriesType
  xLabel?: string // Label for the x axis
  xName: string // Field name from the data to be used for the x axis
  yColor: string[] // Each yName should have a color
  yLabel?: string //Label for the y axis
  yName: string[] // Field name from the data to be used for the y axis
}

const ChartComponent: React.FC<ChartComponentProps> = ({
  className,
  columnSpacing,
  columnWidth,
  cornerRadius,
  data,
  enableTooltip = true,
  height,
  id,
  maxXAxis,
  maxYAxis,
  name,
  seriesWidth,
  title,
  tooltipFormat,
  tooltipTemplate,
  type,
  xLabel,
  xName,
  yColor,
  yLabel,
  yName,
}) => {
  const labelStyle = {
    color: '#34363D',
    size: '12px',
    fontWeight: '600',
    fontFamily: 'Inter',
  }
  return (
    <SyncfusionChart
      id={id}
      enablePersistence={true}
      className={`chart-component ${className}`}
      chartArea={{ border: { width: 0 } }}
      height={height}
      enableAnimation={false}
      legendSettings={{
        visible: true,
        position: 'Bottom',
        margin: { bottom: 0 },
        height: '30px',
        textStyle: {
          color: 'var(--gray-600)',
          size: '12px',
          fontWeight: '400',
          fontFamily: 'Inter',
        },
      }}
      name={name}
      title={title}
      primaryXAxis={{
        valueType: 'Category',
        title: xLabel,
        maximum: maxXAxis,
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        lineStyle: { width: 2 },
        labelStyle,
      }}
      primaryYAxis={{
        title: yLabel,
        maximum: maxYAxis,
        lineStyle: { width: 2, dashArray: '5,0' },
        majorGridLines: { width: 1, dashArray: '5,3' },
        labelStyle,
      }}
      tooltip={{
        enable: enableTooltip,
        format: tooltipFormat,
        template: tooltipTemplate,
      }}
    >
      <Inject
        services={[
          StackingColumnSeries,
          ColumnSeries,
          Tooltip,
          LineSeries,
          Category,
          Legend,
          DataLabel,
        ]}
      />
      <SeriesCollectionDirective>
        {yName.map((y, index) => (
          <SeriesDirective
            key={y}
            name={y}
            dataSource={data}
            xName={xName}
            yName={y}
            fill={yColor[index]}
            columnSpacing={columnSpacing}
            columnWidth={columnWidth}
            width={seriesWidth}
            animation={{ enable: false }}
            legendShape="Circle"
            type={type}
            cornerRadius={cornerRadius}
          />
        ))}
      </SeriesCollectionDirective>
    </SyncfusionChart>
  )
}

export default ChartComponent
